import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, Presentation, FileText, Database, Printer, BarChart3 } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";

export default function Reports() {
  const { pid } = useParams();
  const [evm, setEvm] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get(`/projects/${pid}/evm`).then(({ data }) => setEvm(data)); }, [pid]);

  const run = async (key, path, filename) => {
    setBusy(key);
    try {
      await downloadFile(path, filename);
      toast.success(`${filename} generated`);
    } catch (e) {
      toast.error("Export failed");
    } finally { setBusy(""); }
  };

  const CARDS = [
    {
      key: "pptx", icon: Presentation, title: "Board-Ready PowerPoint",
      desc: "Executive summary, KPI tiles, S-curve chart, quantity variance table, delay drivers and critical path focus — one click.",
      action: () => run("pptx", `/reports/pptx/${pid}`, "plan-sarthi-review.pptx"), label: "Generate .PPTX", testId: "export-pptx-btn",
    },
    {
      key: "excel", icon: FileSpreadsheet, title: "Full Excel Data Pack",
      desc: "7 sheets: executive summary, WBS & quantities, CPM schedule, DPR log, hindrance register, S-curve data and store inventory.",
      action: () => run("excel", `/reports/excel/${pid}`, "plan-sarthi-pack.xlsx"), label: "Generate .XLSX", testId: "export-excel-btn",
    },
    {
      key: "pdf4", icon: FileText, title: "A4 Print-Ready PDF",
      desc: "Site-printable status report with EVM metrics, quantity progress, critical path and hindrance register.",
      action: () => run("pdf4", `/reports/pdf/${pid}?size=A4`, "plan-sarthi-report-A4.pdf"), label: "Generate A4 PDF", testId: "export-pdf-a4-btn",
    },
    {
      key: "pdf3", icon: Printer, title: "A3 Landscape PDF",
      desc: "Wide-format print for site war rooms and review boards.",
      action: () => run("pdf3", `/reports/pdf/${pid}?size=A3`, "plan-sarthi-report-A3.pdf"), label: "Generate A3 PDF", testId: "export-pdf-a3-btn",
    },
    {
      key: "odata", icon: Database, title: "Power BI / OData Feed",
      desc: "Live JSON feed of activities, schedule, EVM, S-curve and inventory for Power BI Desktop 'Web' connector.",
      action: () => run("odata", `/reports/odata/${pid}`, "plan-sarthi-odata.json"), label: "Download feed", testId: "export-odata-btn",
    },
  ];

  return (
    <div data-testid="reports-page">
      <PageHead title="One-Click Export & Dashboard Center" subtitle="Weekend decks in seconds — no manual compilation." />

      {evm ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <Kpi label="Physical progress" value={`${evm.percent_complete}%`} tone="primary" />
          <Kpi label="SPI" value={evm.spi} tone={evm.spi >= 0.95 ? "good" : "bad"} />
          <Kpi label="CPI" value={evm.cpi} tone={evm.cpi >= 1 ? "good" : "bad"} />
          <Kpi label="Earned value" value={fmtMoney(evm.ev)} />
          <Kpi label="Forecast EAC" value={fmtMoney(evm.eac)} />
        </div>
      ) : null}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {CARDS.map((c) => (
          <div key={c.key} className="border border-border bg-card p-4 flex flex-col rise" data-testid={`export-card-${c.key}`}>
            <c.icon className="w-5 h-5 text-primary" />
            <h3 className="font-semibold mt-2.5">{c.title}</h3>
            <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed flex-1">{c.desc}</p>
            <Button className="mt-4" onClick={c.action} disabled={busy === c.key} data-testid={c.testId}>
              {busy === c.key ? "Generating…" : c.label}
            </Button>
          </div>
        ))}

        <Panel title="Power BI connection steps" testId="powerbi-panel">
          <ol className="text-[12px] text-muted-foreground space-y-1.5 list-decimal pl-4">
            <li>Power BI Desktop → Get Data → Web.</li>
            <li>Paste the OData URL below and use the anonymous/Bearer credential.</li>
            <li>Expand the <span className="num">activities</span>, <span className="num">schedule</span> and <span className="num">scurve</span> records.</li>
            <li>Refresh on schedule for live field data.</li>
          </ol>
          <div className="mt-3 border border-border bg-muted/50 p-2 num text-[11px] break-all">
            {process.env.REACT_APP_BACKEND_URL}/api/reports/odata/{pid}
          </div>
          <div className="flex gap-1.5 mt-3">
            <Badge2 tone="primary"><BarChart3 className="w-3 h-3 mr-1" /> Live feed</Badge2>
            <Badge2 tone="good">Matrix ready</Badge2>
          </div>
        </Panel>
      </div>
    </div>
  );
}
